import React from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import {
  EMPLOYEE_PAYROLL_ROUTE,
  EMPLOYEES_ROUTE,
  FINANCE_EMPLOYEE_PAYROLL_ROUTE,
  FINANCE_PAYROLL_ROUTE,
  getPayrollEmployeeDestination,
  getPayrollListDestination,
} from './production/employees/lib/routes'
import { getLegacyRouteSheetSearch } from '~/features/production/route-sheet-actions/legacy-route-sheet-links'
import { NotFound } from './not-found'
import { RegistrationPage } from './registration'
import Users from './users'
import { UserEditFeature } from '~/features/user-edit'
import { RoleEditFeature } from '~/features/role-edit'
import { AssemblyByType } from '~/features/assembly-by-type'
import { ShippingOrderById } from '~/features/delivery/shipping-order-by-id/ui/ShippingOrderById'
import UploadReport from './upload-report'
import { TabsValidationProvider } from '~/shared/lib/contexts/tabs-validation-context/provider'
import {
  isDevelopmentMode,
  isStagingEnvironment,
} from '~/shared/lib/environment'
import { ProductOptionsProvider } from '~/features/products/controllers/options-context'
import { CheckoutProvider } from './orders/lib/context/create-order-context'

const LoginPage = React.lazy(() => import('./login'))
const MainPage = React.lazy(() => import('./main'))
const Assembli = React.lazy(() => import('./assembling'))
const Delivery = React.lazy(() => import('./delivery'))

const ProductsPage = React.lazy(() => import('./products'))
const OptionsPage = React.lazy(() => import('./options'))
const OptionCreatePage = React.lazy(
  () => import('./options-crud/options-create'),
)
const OptionEditPage = React.lazy(() => import('./options-crud/options-edit'))

const KvantsPage = React.lazy(() => import('./kvants'))
const KvantsCrudPage = React.lazy(() => import('./kvants-crud/kvants-create'))

const SettingsPage = React.lazy(() => import('./settings'))
const TicketsPage = React.lazy(() => import('./tickets'))
const TicketDetailsPage = React.lazy(() => import('./tickets/ticket-details'))
const RoleCreate = React.lazy(() => import('./role-create'))
const FBOCreate = React.lazy(() => import('./fbo-create/index'))
const FBOWriteOff = React.lazy(() => import('./fbo-write-off'))
const UsersSlug = React.lazy(() => import('./users/user-details'))
const ProfilePage = React.lazy(() => import('./profile'))
const CategoriesCrudPage = React.lazy(() => import('./categories-crud'))
const ProductsCategories = React.lazy(() => import('./categories'))
const ProductsCrudPage = React.lazy(() => import('./products-crud'))

const KitsPage = React.lazy(() => import('./kits'))
const CreateKitsPage = React.lazy(() => import('./crud-kits/create-kits'))
const EditKitsPage = React.lazy(() => import('./crud-kits/edit-kits'))

const ShopsPage = React.lazy(() => import('./stores'))
const StoresCrudPage = React.lazy(() => import('./stores-crud'))

const InformationPage = React.lazy(() => import('./information'))
const InformationCrudPage = React.lazy(
  () => import('./information-crud/information-create'),
)
const InformationEditPage = React.lazy(
  () => import('./information-crud/information-edit'),
)

const OrdersPage = React.lazy(() => import('./orders'))
const OrderSplitPage = React.lazy(() => import('./orders-crud/order-split'))

const ReturnsPage = React.lazy(() => import('./returns'))
const CreateReturnPage = React.lazy(
  () => import('./returns-crud/create-return'),
)
const EditReturnPage = React.lazy(() => import('./returns-crud/edit-return'))

const RetailPage = React.lazy(() => import('./retail'))
const FBOPage = React.lazy(() => import('./fbo'))
const OrdersCrudPage = React.lazy(() => import('./orders-crud'))
const KvantEditPage = React.lazy(() => import('./kvants-crud/kvants-edit'))
const InventoryById = React.lazy(() => import('./inventory-by-id/index'))

const WarehousesPage = React.lazy(() => import('./warehouses'))
const InventoryPage = React.lazy(() => import('./inventory'))
const InventoryCreate = React.lazy(() => import('./inventory-create/index'))
const AssemblyByIdPage = React.lazy(
  () => import('./assembling/assembly-by-id-page'),
)
const OrderFboView = React.lazy(() => import('./fbo-view/index'))
const OrderFboAssembling = React.lazy(() => import('./fbo-assembling'))

const MarketingPage = React.lazy(
  () => import('./marketing/marketing-discounts/index'),
)
const MarketingCoupons = React.lazy(
  () => import('./marketing/marketing-coupons/index'),
)
const MarketingSlider = React.lazy(
  () => import('./marketing/marketing-slider/index'),
)
const CreateNewArrival = React.lazy(
  () => import('./warehouse-crud/createNewArrival'),
)
const ViewingReceipt = React.lazy(
  () => import('./warehouse-crud/viewingReceipts'),
)
const ReportsPage = React.lazy(() => import('./reports'))

const BadgesPage = React.lazy(() => import('./badges'))

const CreateBadges = React.lazy(() => import('./badges-crud/create-badges'))

const EditBadges = React.lazy(() => import('./badges-crud/edit-badges'))

const AbandonedPage = React.lazy(() => import('./abandoned'))

const AutopurchasesPage = React.lazy(() => import('./autopurchases'))

const PurchasePage = React.lazy(() => import('./purchase'))

const CustomersPage = React.lazy(() => import('./customers'))
const ReviewsPage = React.lazy(() => import('./reviews'))

const EditCustomerPage = React.lazy(
  () => import('./crud-customers/edit-customer'),
)

const UserLogsActivitiesPage = React.lazy(
  () => import('./user-logs-activities'),
)
const RealtimeDebugPage = React.lazy(() => import('./realtime-debug'))
const UiKitPage = React.lazy(() => import('./ui-kit'))

const MaterialsPage = React.lazy(() => import('./materials'))
const UpdateMaterialPage = React.lazy(() => import('./crud-materials'))

const FeaturePage = React.lazy(() => import('./feature-page'))
const UpdateFeaturePage = React.lazy(() => import('./crud-feature-page'))

const SettingShipping = React.lazy(() => import('./setting-shipping'))

const SettingShippingEdit = React.lazy(
  () => import('./crud-setting-shipping/edit-setting-shipping'),
)

const CreateOrderPage = React.lazy(() => import('./orders/create-order'))

const BrandsPage = React.lazy(() => import('./brands'))
const BlogPostsPage = React.lazy(() => import('./blog-posts'))
const BlogTagsPage = React.lazy(() => import('./blog-tags'))
const BlogPostCreatePage = React.lazy(() => import('./blog-posts-create'))
const BlogPostEditPage = React.lazy(() => import('./blog-posts-edit'))

const CreateBrandsPage = React.lazy(() => import('./crud-brands'))

const EditBrandsPage = React.lazy(() => import('./crud-brands'))

const CostingProductsPage = React.lazy(() => import('./costing/products'))
const CostingProductDetailsPage = React.lazy(
  () => import('./costing/product-details'),
)
const CostingSpecificationDetailsPage = React.lazy(
  () => import('./costing/specification-details'),
)
const CostingMaterialsPage = React.lazy(() => import('./costing/materials'))
const CostingMaterialDetailsPage = React.lazy(
  () => import('./costing/material-details'),
)
const CostingOperationsPage = React.lazy(() => import('./costing/operations'))
const CostingSuppliersPage = React.lazy(() => import('./costing/suppliers'))
const CostingOperationDetailsPage = React.lazy(
  () => import('./costing/operation-details'),
)
const PayrollPage = React.lazy(() => import('./finance/payroll'))
const ProductionRouteSheetsPage = React.lazy(
  () => import('./production/route-sheets'),
)
const ProductionEmployeesPage = React.lazy(
  () => import('./production/employees'),
)
const ProductionInteroperationPage = React.lazy(
  () => import('./production/interoperation'),
)
const ProductionHonestSignsPage = React.lazy(
  () => import('./production/honest-signs'),
)
const ProductionAnalyticsPage = React.lazy(
  () => import('./production/analytics'),
)

const CreateBrandsPageWithValidation = () => (
  <TabsValidationProvider>
    <CreateBrandsPage />
  </TabsValidationProvider>
)

const EditBrandsPageWithValidation = () => (
  <TabsValidationProvider>
    <EditBrandsPage />
  </TabsValidationProvider>
)

const CreateOrderPageWithValidation = () => (
  <CheckoutProvider>
    <TabsValidationProvider>
      <CreateOrderPage />
    </TabsValidationProvider>
  </CheckoutProvider>
)

const CreateKitsPageWithValidation = () => (
  <TabsValidationProvider>
    <CreateKitsPage />
  </TabsValidationProvider>
)

const EditKitsPageWithValidation = () => (
  <TabsValidationProvider>
    <EditKitsPage />
  </TabsValidationProvider>
)

const ProductsCrudPageWithProviders = () => (
  <ProductOptionsProvider>
    <TabsValidationProvider>
      <ProductsCrudPage />
    </TabsValidationProvider>
  </ProductOptionsProvider>
)

const CreateOptionPageWithValidation = () => (
  <TabsValidationProvider>
    <OptionCreatePage />
  </TabsValidationProvider>
)

const EditOptionPageWithValidation = () => (
  <TabsValidationProvider>
    <OptionEditPage />
  </TabsValidationProvider>
)

const SettingShippingEditWithValidation = () => (
  <TabsValidationProvider>
    <SettingShippingEdit />
  </TabsValidationProvider>
)

const CreateBlogPostPageWithValidation = () => (
  <TabsValidationProvider>
    <BlogPostCreatePage />
  </TabsValidationProvider>
)

const EditBlogPostPageWithValidation = () => (
  <TabsValidationProvider>
    <BlogPostEditPage />
  </TabsValidationProvider>
)

const CategoriesCrudPageWithValidation = () => (
  <TabsValidationProvider>
    <CategoriesCrudPage />
  </TabsValidationProvider>
)

const CreateNewArrivalWithValidation = () => (
  <TabsValidationProvider>
    <CreateNewArrival />
  </TabsValidationProvider>
)

const isUiKitRouteEnabled = isDevelopmentMode || isStagingEnvironment

const optionalRoutes = isUiKitRouteEnabled
  ? [
      {
        Component: UiKitPage,
        path: '/ui-kit',
      },
    ]
  : []

export const publicRoutes = [
  {
    Component: LoginPage,
    path: '/login',
  },
  {
    Component: RegistrationPage,
    path: '/registration',
  },
]

const PayrollListRedirect = () => {
  const [params] = useSearchParams()
  return <Navigate to={getPayrollListDestination(params)} replace />
}

const PayrollEmployeeRedirect = () => {
  const { employeeId = '' } = useParams<{ employeeId: string }>()
  const [params] = useSearchParams()
  return (
    <Navigate to={getPayrollEmployeeDestination(employeeId, params)} replace />
  )
}

const ProductionLaunchedRedirect = () => (
  <Navigate to="/production/route-sheets?tab=active" replace />
)

const ProductionInProgressRedirect = () => {
  const [params] = useSearchParams()
  return (
    <Navigate
      to={`/production/route-sheets?${getLegacyRouteSheetSearch(params, 'in-progress')}`}
      replace
    />
  )
}

const ProductionQualityControlRedirect = () => {
  const [params] = useSearchParams()
  return (
    <Navigate
      to={`/production/route-sheets?${getLegacyRouteSheetSearch(params, 'quality-control')}`}
      replace
    />
  )
}

const ProductionRouteSheetRedirect = () => {
  const { id = '' } = useParams<{ id: string }>()
  const search = new URLSearchParams({ sheet: id }).toString()

  return (
    <Navigate
      to={{
        pathname: '/production/route-sheets',
        search: `?${search}`,
      }}
      replace
    />
  )
}

export const privateRoutes = [
  {
    Component: MainPage,
    path: '/',
  },

  // Autopurchases
  {
    Component: AutopurchasesPage,
    path: '/autopurchases',
  },

  // Products
  {
    Component: ProductsPage,
    path: '/products',
  },
  {
    Component: ProductsCrudPageWithProviders,
    path: '/products/create',
  },
  {
    Component: ProductsCrudPageWithProviders,
    path: '/products/:id/edit',
  },
  {
    Component: OptionsPage,
    path: '/options',
  },
  {
    Component: CreateOptionPageWithValidation,
    path: '/options/create',
  },
  {
    Component: EditOptionPageWithValidation,
    path: '/options/:id',
  },
  {
    Component: ProductsCategories,
    path: '/categories',
  },

  // Categories
  {
    Component: CategoriesCrudPageWithValidation,
    path: '/categories/:id/edit',
  },
  {
    Component: CategoriesCrudPageWithValidation,
    path: '/categories/create',
  },

  // Kits
  {
    Component: KitsPage,
    path: 'kits',
  },
  {
    Component: CreateKitsPageWithValidation,
    path: '/kits/create',
  },
  {
    Component: EditKitsPageWithValidation,
    path: '/kits/:id/edit',
  },

  // Materials
  {
    Component: MaterialsPage,
    path: '/materials',
  },
  {
    Component: UpdateMaterialPage,
    path: '/materials/create',
  },
  {
    Component: UpdateMaterialPage,
    path: '/materials/:id/view',
  },

  // Kvants
  {
    Component: KvantsPage,
    path: '/kvants',
  },
  {
    Component: KvantEditPage,
    path: '/kvants/:id',
  },
  {
    Component: KvantsCrudPage,
    path: '/kvants/create',
  },

  // Stores
  {
    Component: ShopsPage,
    path: '/stores',
  },
  {
    Component: StoresCrudPage,
    path: '/stores/:id/edit',
  },

  {
    Component: SettingShipping,
    path: '/settings-shipping',
  },

  {
    Component: SettingShippingEditWithValidation,
    path: '/settings-shipping/:code/:storeId/zone',
  },

  // InformationPage
  {
    Component: InformationPage,
    path: '/information',
  },
  {
    Component: InformationCrudPage,
    path: '/information/create',
  },
  {
    Component: InformationEditPage,
    path: '/information/:id',
  },

  // Orders
  {
    Component: OrdersPage,
    path: '/orders',
  },
  {
    Component: OrderSplitPage,
    path: '/orders/divide/:id',
  },
  {
    Component: OrdersCrudPage,
    path: '/orders/:id',
  },
  {
    Component: CreateOrderPageWithValidation,
    path: '/orders/create',
  },

  // Returns
  {
    Component: ReturnsPage,
    path: '/returns',
  },
  {
    Component: CreateReturnPage,
    path: '/returns/create',
  },
  {
    Component: EditReturnPage,
    path: '/returns/:id',
  },

  // Retail
  {
    Component: RetailPage,
    path: '/retail',
  },

  // Warehouses
  {
    Component: WarehousesPage,
    path: '/incoming',
  },

  // Inventory
  {
    Component: InventoryById,
    path: '/inventory/:id',
  },
  {
    Component: InventoryPage,
    path: '/inventory',
  },
  {
    Component: InventoryCreate,
    path: '/inventory/create',
  },

  // Arrival
  {
    Component: CreateNewArrivalWithValidation,
    path: '/incoming/create',
  },

  // Receipt
  {
    Component: ViewingReceipt,
    path: '/incoming/:number/view',
  },

  // Marketing
  {
    Component: MarketingPage,
    path: '/discounts',
  },
  {
    Component: MarketingCoupons,
    path: '/coupons',
  },
  {
    Component: MarketingSlider,
    path: '/slider',
  },

  // Delivery
  {
    Component: Delivery,
    path: 'shipping/orders',
  },
  { path: '/shipping/checking/:id/view', Component: ShippingOrderById },

  // Activity
  {
    Component: UserLogsActivitiesPage,
    path: '/activity',
  },
  {
    Component: RealtimeDebugPage,
    path: '/realtime-debug',
  },

  // FBO
  { path: '/fbo/create', Component: FBOCreate },
  { path: '/fbo/write-off', Component: FBOWriteOff },
  {
    Component: FBOPage,
    path: '/fbo',
  },
  {
    Component: OrderFboView,
    path: '/shipping/:id/bulk/view',
  },
  {
    Component: OrderFboAssembling,
    path: '/shipping/:id/bulk/assembling',
  },

  // Assembly
  {
    Component: AssemblyByType,
    path: '/assembly/orders/:type',
  },
  {
    Component: AssemblyByIdPage,
    path: '/assembly/:id/items',
  },
  {
    Component: Assembli,
    path: '/assembly/orders',
  },

  // Reports
  {
    Component: ReportsPage,
    path: '/reports',
  },
  {
    Component: UploadReport,
    path: '/upload-report',
  },

  // Badges
  {
    Component: BadgesPage,
    path: '/badges',
  },
  {
    Component: CreateBadges,
    path: '/badges/create',
  },
  {
    Component: EditBadges,
    path: '/badges/:id/edit',
  },

  // Abandoned

  {
    Component: AbandonedPage,
    path: '/abandoned',
  },
  {
    Component: PurchasePage,
    path: '/purchase',
  },

  // Customers
  {
    Component: CustomersPage,
    path: '/customers',
  },
  {
    Component: ReviewsPage,
    path: '/reviews',
  },
  {
    Component: EditCustomerPage,
    path: '/customers/:id',
  },

  // Feature
  {
    Component: FeaturePage,
    path: '/feature',
  },
  {
    Component: UpdateFeaturePage,
    path: '/feature/create',
  },
  {
    Component: UpdateFeaturePage,
    path: '/feature/:id/view',
  },

  // Brands
  {
    Component: BrandsPage,
    path: '/brands',
  },
  {
    Component: BlogPostsPage,
    path: '/blog/posts',
  },
  {
    Component: BlogTagsPage,
    path: '/blog/tags',
  },
  {
    Component: CreateBlogPostPageWithValidation,
    path: '/blog/posts/create',
  },
  {
    Component: EditBlogPostPageWithValidation,
    path: '/blog/posts/:id',
  },
  {
    Component: CreateBrandsPageWithValidation,
    path: '/brands/create',
  },
  {
    Component: EditBrandsPageWithValidation,
    path: '/brands/:id/edit',
  },

  // Finance
  { Component: PayrollListRedirect, path: FINANCE_PAYROLL_ROUTE },
  {
    Component: PayrollEmployeeRedirect,
    path: FINANCE_EMPLOYEE_PAYROLL_ROUTE,
  },

  // Production
  {
    Component: ProductionLaunchedRedirect,
    path: '/production/launched',
  },
  {
    Component: ProductionRouteSheetsPage,
    path: '/production/route-sheets',
  },
  {
    Component: ProductionRouteSheetRedirect,
    path: '/production/route-sheets/:id',
  },
  {
    Component: ProductionInProgressRedirect,
    path: '/production/in-progress',
  },
  {
    Component: ProductionEmployeesPage,
    path: EMPLOYEES_ROUTE,
  },
  {
    Component: ProductionInteroperationPage,
    path: '/production/interoperation',
  },
  {
    Component: PayrollPage,
    path: EMPLOYEE_PAYROLL_ROUTE,
  },
  {
    Component: ProductionQualityControlRedirect,
    path: '/production/quality-control',
  },
  {
    Component: ProductionHonestSignsPage,
    path: '/production/honest-signs',
  },
  {
    Component: ProductionAnalyticsPage,
    path: '/production/analytics',
  },

  // Costing
  {
    Component: CostingProductsPage,
    path: '/costing/products',
  },
  {
    Component: CostingProductDetailsPage,
    path: '/costing/products/create',
  },
  {
    Component: CostingProductDetailsPage,
    path: '/costing/products/:id',
  },
  {
    Component: CostingSpecificationDetailsPage,
    path: '/costing/specifications/:id',
  },
  {
    Component: CostingMaterialsPage,
    path: '/costing/materials',
  },
  {
    Component: CostingMaterialDetailsPage,
    path: '/costing/materials/create',
  },
  {
    Component: CostingMaterialDetailsPage,
    path: '/costing/materials/:id',
  },
  {
    Component: CostingOperationsPage,
    path: '/costing/operations',
  },
  {
    Component: CostingSuppliersPage,
    path: '/costing/suppliers',
  },
  {
    Component: CostingOperationDetailsPage,
    path: '/costing/operations/create',
  },
  {
    Component: CostingOperationDetailsPage,
    path: '/costing/operations/:id',
  },

  // User
  {
    Component: Users,
    path: '/users',
  },
  { path: '/users/:id/edit', Component: UserEditFeature },
  { path: '/user/create', Component: UsersSlug },
  { path: '/profile/:id/edit', Component: ProfilePage },

  // Role
  { path: '/role/create', Component: RoleCreate },
  { path: '/userRoles/:id/edit', Component: RoleEditFeature },
  { path: '/userRoles/:id/edit', Component: RoleEditFeature },

  {
    Component: SettingsPage,
    path: '/settings',
  },
  {
    Component: TicketsPage,
    path: '/tickets',
  },
  {
    Component: TicketDetailsPage,
    path: '/tickets/:ticketId',
  },
  ...optionalRoutes,
  {
    Component: NotFound,
    path: '/*',
  },
]
